/**
 * Pathologist-grade tiled WSI viewport.
 *
 * Replaces the single-file "download and draw" WSIViewport with a
 * pyramid-aware renderer:
 *
 *   - Builds a pyramid index from the series' per-instance metadata.
 *   - Picks the level whose scale ≈ current zoom (mipmap rule).
 *   - For each animation frame, computes which tiles are visible and
 *     draws whichever of them are cached.  Missing tiles are requested
 *     async; once they arrive the next redraw paints them in — the
 *     visible set that's on screen stays put meanwhile, so pan / zoom
 *     doesn't stutter.
 *   - Top bar with live magnification readout + preset chips,
 *     vertical HALO-style annotation toolbar, minimap, scalebar, and
 *     cursor HUD stay identical to the earlier WSIViewport chrome.
 *
 * The annotation SVG overlay shares the viewport's pan/zoom transform
 * so shapes drawn at level 0 coordinates co-register at every level.
 */
import { createEffect, createResource, createSignal, For, Index, onCleanup, onMount, Show } from "solid-js";
import { IconEdit, IconTrash } from "@viewer/ui/Icons";
import {
  createAnnotationStore, type PathAnno, type PathologyTool,
  type AnnotationClass,
} from "./annotations";
import type { AnnotationClass as AC } from "./annotations";
import type { StudyInstance } from "@viewer/api/client";
import {
  buildPyramid, cachedTile, getTile, getTilesBatch, inflightCount,
  pickLevel, prioritizeTiles, tileIsSettled, visibleTiles,
  type Pyramid, type PyramidLevel,
} from "./pyramid";
import { MagObjectiveRow, MagObjectiveDropdown } from "./MagObjectivePicker";

export type WSIAnnotationStore = ReturnType<typeof createAnnotationStore>;

export interface WSIPyramidViewportProps {
  instances:   StudyInstance[];
  slideLabel?: string;
  store:       WSIAnnotationStore;
  activeClass: AnnotationClass;
  /** Fullscreen mode. When true, the viewport hides its own chrome
   *  (top bar + annotation rail) and the parent shell is expected to
   *  have hidden its tray + dock. The overview minimap stays visible
   *  on both desktop and mobile — shrunk further on mobile so it
   *  barely touches the working surface. A single ⛶/⤢ button in the
   *  viewport's top-right toggles this via `onToggleFullscreen`. */
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  /** Snapshot callback — fired when the user clicks the camera
   *  button in the annotation rail. The viewport passes a composited
   *  dataURL (canvas pixels + SVG overlay rasterised) plus the
   *  current pan/zoom so the parent can persist + restore the
   *  exact view later. */
  onSnapshot?: (snap: {
    dataUrl: string;
    panX: number;
    panY: number;
    zoom: number;
  }) => void;
  /** Restore a previously-captured view — fired by the parent when
   *  the user clicks a snapshot thumbnail. The viewport installs
   *  the given pan/zoom and re-renders. */
  onViewportReady?: (api: {
    setView: (v: { panX: number; panY: number; zoom: number }) => void;
    getView: () => { panX: number; panY: number; zoom: number };
  }) => void;
  /** Navigate-to-annotation callback — fired when the user clicks a
   *  row in the floating annotations quick-nav panel. The parent
   *  shell computes the bounding-box centre + fit-to-viewport zoom
   *  and calls viewApi.setView. Left optional so non-pathology
   *  callers don't have to wire it. */
  onNavigateTo?: (a: import("./annotations").PathAnno) => void;
  /** Class palette — inline editor in the floating ROI panel lets
   *  the reader re-class any annotation (chip click overwrites both
   *  colour and label to match the chosen class). Left optional so
   *  non-pathology callers don't have to wire it; panel just hides
   *  the chip row when empty. */
  classes?: ReadonlyArray<AnnotationClass>;
  /** Reserve this many pixels on the right edge for shell chrome
   *  (typically the dock width when the dock is open). The
   *  overview minimap + tool rail anchor to window-right minus
   *  this reserve so they're pushed out of the dock's footprint
   *  as the reader drags it wider. Falls back to viewport-local
   *  positioning when 0 or omitted. */
  rightReserve?: number;
}

// Objective-equivalent magnification presets matching the clinical
// workflow ladder (see research brief attached to commit
// "pathology magnification ladder"):
//
//   1×   — slide-in-hand overview
//   2×   — low-power scanning
//   4×   — ROI hunting
//   10×  — H&E tissue architecture
//   20×  — tumour grading, IHC context
//   40×  — nuclear scoring, mitotic figures (matches a 0.25 MPP scan 1:1)
//   100× — chromatin, nucleoli, infectious organisms (digital zoom
//          beyond most scanned data; label it "(digital)" in the UI)
const MAG_PRESETS = [1, 2, 4, 10, 20, 40, 100];

export function WSIPyramidViewport(p: WSIPyramidViewportProps) {
  let mainCvs!: HTMLCanvasElement;
  let miniCvs!: HTMLCanvasElement;
  let container!: HTMLDivElement;
  let overlaySvg!: SVGSVGElement;

  const [pyr] = createResource(() => p.instances, (inst) => buildPyramid(inst));

  // Zoom = screen px per slide (level-0) px. 1.0 = native base resolution.
  const [zoom, setZoom] = createSignal(1);
  const [panX, setPanX] = createSignal(0);
  const [panY, setPanY] = createSignal(0);
  const [tool, setTool] = createSignal<PathologyTool>("move");
  const [cursorX, setCursorX] = createSignal(0);
  const [cursorY, setCursorY] = createSignal(0);
  const [magInput, setMagInput] = createSignal("");
  // Inflight tile count — repolled on each draw so the loading badge
  // updates as decodes land. `draw()` writes `loadingStats` which
  // captures the current-frame visible/missing tally for the overlay.
  const [inflight, setInflight] = createSignal(0);
  const [loadingStats, setLoadingStats] = createSignal({
    missingInView: 0, visibleCount: 0, totalVisible: 0, partialRender: false,
  });

  // --- annotation draw state ---
  type Stroke =
    | { kind: "drag2"; a: { x: number; y: number }; b: { x: number; y: number } }
    | { kind: "polygon"; pts: { x: number; y: number }[]; live: { x: number; y: number } }
    | { kind: "freehand"; pts: { x: number; y: number }[] }
    | null;
  const [stroke, setStroke] = createSignal<Stroke>(null);

  // While the user hasn't panned/zoomed yet, the viewport is still in
  // "auto-fit" mode and every canvas resize re-centres the slide.
  // Any pointer / wheel interaction flips this latch off so the
  // viewport stops stealing pan/zoom from the user.
  const [autoFit, setAutoFit] = createSignal(true);
  const [railOpen, setRailOpen] = createSignal(true);
  const [miniOpen, setMiniOpen] = createSignal(true);

  // Minimap positioning. `miniPos` is null by default, which makes
  // the minimap sit at the bottom-right corner via Tailwind classes
  // (plus a mobile-fullscreen safe-area offset so Android Firefox's
  // URL bar doesn't cover it). Once the user drags the overview
  // strip, we switch to absolute `{left, top}` from this signal.
  const [miniPos, setMiniPos] = createSignal<{ x: number; y: number } | null>(null);
  let miniFrame!: HTMLDivElement;
  let miniDrag: null | { startPX: number; startPY: number; startX: number; startY: number } = null;
  function onMiniGripDown(e: PointerEvent) {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const rect = miniFrame.getBoundingClientRect();
    const parent = miniFrame.offsetParent?.getBoundingClientRect();
    // Track current top-left in PARENT-relative pixels so drags
    // translate cleanly between scroll/resize — subtract the
    // offsetParent's own origin from the viewport rect.
    const x = rect.left - (parent?.left ?? 0);
    const y = rect.top  - (parent?.top  ?? 0);
    miniDrag = { startPX: e.clientX, startPY: e.clientY, startX: x, startY: y };
  }
  function onMiniGripMove(e: PointerEvent) {
    if (!miniDrag) return;
    const dx = e.clientX - miniDrag.startPX;
    const dy = e.clientY - miniDrag.startPY;
    setMiniPos({ x: miniDrag.startX + dx, y: miniDrag.startY + dy });
  }
  function onMiniGripUp(e: PointerEvent) {
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    miniDrag = null;
  }

  // ------------- Floating tool rail (for fullscreen mode) -------------
  // Same pattern as the minimap: a `pos` signal tracks the user's
  // last-known drag location. Null = default (pinned to the right
  // edge via Tailwind classes). Once the user drags the title
  // strip, switch to absolute coordinates.
  const [toolPos, setToolPos] = createSignal<{ x: number; y: number } | null>(null);
  let toolRailEl!: HTMLDivElement;
  let toolDrag: null | { sx: number; sy: number; x0: number; y0: number } = null;
  function onToolGripDown(e: PointerEvent) {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const r = toolRailEl.getBoundingClientRect();
    const par = toolRailEl.offsetParent?.getBoundingClientRect();
    toolDrag = {
      sx: e.clientX, sy: e.clientY,
      x0: r.left - (par?.left ?? 0), y0: r.top - (par?.top ?? 0),
    };
  }
  function onToolGripMove(e: PointerEvent) {
    if (!toolDrag) return;
    setToolPos({ x: toolDrag.x0 + (e.clientX - toolDrag.sx), y: toolDrag.y0 + (e.clientY - toolDrag.sy) });
  }
  function onToolGripUp(e: PointerEvent) {
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    toolDrag = null;
  }

  // ------------- Floating annotations quick-nav panel -------------
  // Always available (not only in fullscreen) — lets the user jump
  // to any ROI from anywhere in the viewer chrome. Collapses to a
  // single pill button when hidden.
  const [annoNavOpen,   setAnnoNavOpen]   = createSignal(false);
  const [editingAnnoId, setEditingAnnoId] = createSignal<string | null>(null);
  const [annoNavPos,  setAnnoNavPos]  = createSignal<{ x: number; y: number } | null>(null);
  let annoNavEl!: HTMLDivElement;
  let annoNavDrag: null | { sx: number; sy: number; x0: number; y0: number } = null;
  function onAnnoNavGripDown(e: PointerEvent) {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const r = annoNavEl.getBoundingClientRect();
    const par = annoNavEl.offsetParent?.getBoundingClientRect();
    annoNavDrag = {
      sx: e.clientX, sy: e.clientY,
      x0: r.left - (par?.left ?? 0), y0: r.top - (par?.top ?? 0),
    };
  }
  function onAnnoNavGripMove(e: PointerEvent) {
    if (!annoNavDrag) return;
    setAnnoNavPos({ x: annoNavDrag.x0 + (e.clientX - annoNavDrag.sx),
                    y: annoNavDrag.y0 + (e.clientY - annoNavDrag.sy) });
  }
  function onAnnoNavGripUp(e: PointerEvent) {
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    annoNavDrag = null;
  }

  function fitToViewport() {
    const pp = pyr();
    if (!pp || !mainCvs) return;
    const w = mainCvs.clientWidth, h = mainCvs.clientHeight;
    if (w === 0 || h === 0) return;
    const fitZ = Math.min(w / pp.slideWidth, h / pp.slideHeight) * 0.96;

    // Single-level-pyramid escape hatch. If the coarsest level we
    // have is the BASE (no downsampled tiers), whole-slide fit-to-view
    // would require rendering every one of its tiles — on the IHC
    // native Ki67 slide that's 91,665 tiles. Draw-loop guards in
    // draw() would then skip the main fetch and leave a blank canvas.
    //
    // Instead, clamp zoom up so that visibleTiles at mount is bounded
    // by the main LRU budget (same MAX_VISIBLE_TILES the draw loop
    // uses). The user sees the CENTRE of the slide at a readable
    // magnification and can pan to explore — dramatically more useful
    // than a black rectangle. When a proper multi-level pyramid lands
    // backend-side, fitZ is already tiny enough that the clamp is a
    // no-op and full-slide fit is restored.
    // fitToViewport targets a tile budget COMFORTABLY below the
    // draw-loop gate (512) so the gate doesn't block the render on
    // mount. Aiming for ~200 tiles gives a healthy margin and keeps
    // the viewport painting across slight layout wobbles.
    const FIT_TILE_TARGET = 200;
    const coarsest = pp.levels[pp.levels.length - 1];
    const coarseTiles = coarsest.tilesPerRow * coarsest.tilesPerCol;
    let z = fitZ;
    if (coarseTiles > FIT_TILE_TARGET) {
      const base = pp.levels[0];
      const minZ = Math.sqrt((w * h) / (FIT_TILE_TARGET * base.meta.cols * base.meta.rows));
      if (minZ > fitZ) z = minZ;
    }

    setZoom(z);
    setPanX(pp.slideWidth / 2);
    setPanY(pp.slideHeight / 2);
  }

  // ---------- Render loop ----------
  function draw() {
    if (!mainCvs) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = Math.max(1, Math.floor(mainCvs.clientWidth  * dpr));
    const ch = Math.max(1, Math.floor(mainCvs.clientHeight * dpr));
    if (mainCvs.width !== cw || mainCvs.height !== ch) {
      mainCvs.width = cw; mainCvs.height = ch;
    }
    const ctx = mainCvs.getContext("2d");
    if (!ctx) return;
    ctx.save();
    // `high` asks the browser for Lanczos / bicubic instead of
    // default bilinear. Matters past native zoom (1× digital and
    // above) where bilinear visibly smears chromatin edges. Slim +
    // Aperio WebViewer also rely on this for their 2D canvas path
    // before falling back to a WebGL Lanczos3 shader at production
    // scale; for our bespoke viewer the 2D path is sufficient for
    // diagnostic-feel quality at up to 3× digital zoom.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = "#0d1117";
    ctx.fillRect(0, 0, cw, ch);

    const pp = pyr();
    if (!pp) { ctx.restore(); return; }

    // Viewport in slide (level-0) coordinates.
    const z = zoom() * dpr;
    const visW = cw / z, visH = ch / z;
    const x0 = panX() - visW / 2;
    const y0 = panY() - visH / 2;
    const x1 = x0 + visW;
    const y1 = y0 + visH;

    const level = pickLevel(pp, zoom());   // zoom, not z (level picked in CSS-px space)
    const tiles = visibleTiles(pp, level, x0, y0, x1, y1);

    // Two guards against the single-level-pyramid storm on the IHC
    // native slide (91k tiles, no coarser level):
    //
    //   (a) Sub-pixel cutoff. If a tile would render smaller than
    //       ~4 CSS px the user can't see any detail — skip fetch.
    //   (b) Visible-count cap. Past a point, even at "visible" zoom
    //       the count exceeds the main LRU (2048) and we get
    //       eviction thrash: each rAF the evicted tiles look
    //       missing → refetched → evict newer. Capping at
    //       MAX_VISIBLE_TILES bounds the draw-loop cost and keeps
    //       the working set inside the cache. Far outside the
    //       visible window the tiles would anyway be off-screen on
    //       the user's monitor.
    //
    // Both gates short-circuit to just minimap + viewport rect —
    // the user still has spatial context, the browser stays
    // responsive, and tile fetching resumes the moment the user
    // zooms in past the threshold.
    //
    // Long-term cure is backend-side: re-ingest into a real
    // multi-level pyramid so `pickLevel` hands us a coarse level
    // with 10s-100s of tiles instead of a base with 91k.
    const MIN_TILE_PX_ON_SCREEN = 4;
    const MAX_VISIBLE_TILES     = 512;
    const tilePxOnScreen = (level.meta.cols / level.scale) * z / dpr;
    if (tilePxOnScreen < MIN_TILE_PX_ON_SCREEN) {
      // Tiles smaller than ~4 CSS px would be sub-pixel noise; skip
      // the whole main-view fetch and just paint minimap/rectangle.
      ctx.restore();
      drawMinimap();
      return;
    }
    // Over-budget fallback: render the MAX_VISIBLE_TILES tiles NEAREST
    // to the viewport centre rather than skipping the whole view.
    // Partial render + "processing…" badge is dramatically better UX
    // than a black rectangle when the user clicks a low-magnification
    // preset on a single-level pyramid.
    let renderTiles = tiles;
    if (tiles.length > MAX_VISIBLE_TILES) {
      const cx = panX() * level.scale / level.meta.cols;
      const cy = panY() * level.scale / level.meta.rows;
      renderTiles = [...tiles]
        .sort((a, b) => {
          const da = (a.tx - cx) * (a.tx - cx) + (a.ty - cy) * (a.ty - cy);
          const db = (b.tx - cx) * (b.tx - cx) + (b.ty - cy) * (b.ty - cy);
          return da - db;
        })
        .slice(0, MAX_VISIBLE_TILES);
    }

    // Draw whichever tiles are already cached; collect everything else
    // into a single batched fetch. This is the key change that stops a
    // zoom-level switch from stampeding vpd-dicomweb with 50+ parallel
    // requests — the single-threaded Zig HTTP server used to wedge
    // under that burst (TCP accept stopped responding; kubelet saw
    // probe timeouts; viewer proxy returned a wall of 502s). With
    // batching one zoom switch is exactly one HTTP round-trip
    // regardless of tile count.
    const missing: Array<{ tx: number; ty: number }> = [];
    let missingInView = 0;           // for the loading-progress badge
    for (const t of renderTiles) {
      const cached = cachedTile(level, t.tx, t.ty);
      if (!cached) {
        // Don't refetch tiles whose decode has already failed this
        // session — without this guard the failed tile would re-enter
        // `missing[]` on every rAF, which is the entire 2000 rps storm.
        if (!tileIsSettled(level, t.tx, t.ty)) missing.push({ tx: t.tx, ty: t.ty });
        missingInView++;
        continue;
      }
      const tileX0 = t.tx * level.meta.cols / level.scale;
      const tileY0 = t.ty * level.meta.rows / level.scale;
      const tileW  = level.meta.cols / level.scale;
      const tileH  = level.meta.rows / level.scale;
      const sx = cw / 2 + (tileX0 - panX()) * z;
      const sy = ch / 2 + (tileY0 - panY()) * z;
      ctx.drawImage(cached.image, sx, sy, tileW * z, tileH * z);
    }
    // Publish loading stats for the overlay badge + aria-live status.
    setLoadingStats({
      missingInView,
      visibleCount: renderTiles.length,
      totalVisible: tiles.length,
      partialRender: renderTiles.length < tiles.length,
    });
    // Slice big visible sets into ≤16-tile chunks so a single
    // /frames/n1,n2,... request never holds vpd-dicomweb's
    // single-threaded accept loop busy long enough for the kubelet
    // TCP probe to time out. With 16-tile batches (~60 KB total
    // payload at JPEG-85, ~500 ms worst-case serial S3 GETs), the
    // browser fires 5-6 parallel HTTP/2 streams that the ingress
    // load-balances across pods — bursts stay smooth instead of
    // wedging a replica.
    const BATCH_SIZE = 16;
    for (let i = 0; i < missing.length; i += BATCH_SIZE) {
      const chunk = missing.slice(i, i + BATCH_SIZE);
      getTilesBatch(pp, level, chunk, () => redraw())
        .then(() => redraw())
        .catch(() => { /* silent */ });
    }

    // 1-tile prefetch ring — ALSO chunked for the same reason.
    const ringCoords = collectRingCoords(level, renderTiles, 1);
    for (let i = 0; i < ringCoords.length; i += BATCH_SIZE) {
      const chunk = ringCoords.slice(i, i + BATCH_SIZE);
      getTilesBatch(pp, level, chunk, () => redraw())
        .catch(() => { /* best-effort */ });
    }

    // Publish live inflight count for the loading badge (pyramid.ts
    // sees the module-level Map, we just read its size).
    setInflight(inflightCount());

    ctx.restore();
    drawMinimap();

    // Progressive background load — keeps pre-fetching a spiral of
    // tiles around the viewport centre while the user is idle, so
    // that pans and zoom-outs aren't gated on freshly-fetching every
    // new cell. Cancelled + rescheduled on every user interaction
    // (pan/zoom) so we never starve foreground work.
    scheduleBackground();
  }

  // ---------- Progressive background tile load ----------
  // After the visible set + 1-ring prefetch land, we continue
  // fetching tiles in expanding "Chebyshev" rings around the
  // viewport centre (one 16-tile batch per tick). Stops when there
  // are no uncached tiles left inside MAX_BG_RING, or when the user
  // pans/zooms (caller calls `scheduleBackground` again → this reset
  // schedule drops the older timer via `clearTimeout`).
  //
  // Inflight dedup in getTilesBatch guarantees these bg fetches
  // NEVER duplicate an already-in-flight foreground request; the
  // sticky minimap cache + main LRU together hold the working set.
  let bgTimer: ReturnType<typeof setTimeout> | null = null;
  const BG_TICK_MS = 250;
  const BG_MAX_RING = 8;       // 17×17 = 289 bg candidates max
  const BG_BATCH = 16;
  function scheduleBackground() {
    if (bgTimer) clearTimeout(bgTimer);
    bgTimer = setTimeout(runBackgroundTick, BG_TICK_MS);
  }
  function cancelBackground() {
    if (bgTimer) { clearTimeout(bgTimer); bgTimer = null; }
  }
  function runBackgroundTick() {
    bgTimer = null;
    const pp = pyr();
    if (!pp || !mainCvs) return;
    // Respect the same draw-loop gates (don't bg-fetch when at a
    // zoom where foreground tiles would also be gated off).
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const level = pickLevel(pp, zoom());
    const tilePx = (level.meta.cols / level.scale) * (zoom() * dpr) / dpr;
    if (tilePx < 4) return;

    const centerTx = Math.max(0, Math.min(level.tilesPerRow - 1,
      Math.floor(panX() * level.scale / level.meta.cols)));
    const centerTy = Math.max(0, Math.min(level.tilesPerCol - 1,
      Math.floor(panY() * level.scale / level.meta.rows)));

    // Walk expanding rings, pick up to BG_BATCH uncached tiles.
    const next: Array<{ tx: number; ty: number }> = [];
    for (let r = 2; r <= BG_MAX_RING && next.length < BG_BATCH; r++) {
      for (let dy = -r; dy <= r && next.length < BG_BATCH; dy++) {
        for (let dx = -r; dx <= r && next.length < BG_BATCH; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const tx = centerTx + dx, ty = centerTy + dy;
          if (tx < 0 || ty < 0 || tx >= level.tilesPerRow || ty >= level.tilesPerCol) continue;
          if (tileIsSettled(level, tx, ty)) continue;
          next.push({ tx, ty });
        }
      }
    }
    if (next.length === 0) return;        // spiral fully settled inside MAX_RING
    getTilesBatch(pp, level, next, () => redraw()).catch(() => {});
    scheduleBackground();                 // keep ticking until empty
  }

  function drawMinimap() {
    const pp = pyr();
    if (!miniCvs || !pp) return;
    const { slideWidth, slideHeight } = pp;
    const smallest = pp.levels[pp.levels.length - 1];   // coarsest
    // Minimap dimensions — smaller on mobile so it doesn't swallow a
    // chunk of the already-tiny canvas. Desktop keeps the 180 px
    // thumbnail that pathologists calibrate their eye to; mobile
    // gets a 96 px PiP-style overlay. In fullscreen on mobile we
    // shrink further to ~72 px so the minimap lives as a true PiP
    // corner sticker without eating into the slide — the user asked
    // for "very small scale" on mobile fullscreen.
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const TARGET_W = p.fullscreen && isMobile ? 72 : isMobile ? 96 : 180;
    const TARGET_MIN_H = p.fullscreen && isMobile ? 24 : isMobile ? 32 : 60;
    const W = TARGET_W, H = Math.max(TARGET_MIN_H, Math.round(TARGET_W * slideHeight / slideWidth));
    if (miniCvs.width !== W || miniCvs.height !== H) {
      miniCvs.width = W; miniCvs.height = H;
    }
    const ctx = miniCvs.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#0d1117";
    ctx.fillRect(0, 0, W, H);

    // Sanity cap: some ingests produce a single-level pyramid where
    // the "coarsest" level IS the base (tens of thousands of tiles).
    // Without this gate, drawMinimap would fire one getTile() per
    // tile of the base level — on the IDC Ki67 native slide that
    // came to 91,662 individual fetches in 20s AND produced a
    // SolidJS "too much recursion" error from the resulting Promise
    // pile-up. When the coarsest level has more tiles than a minimap
    // canvas can meaningfully show, skip hydration: we still draw
    // the viewport rectangle on the background.
    const MAX_MINIMAP_TILES = 512;
    const coarseTotal = smallest.tilesPerCol * smallest.tilesPerRow;
    const skipMinimapFetch = coarseTotal > MAX_MINIMAP_TILES;
    // Draw whichever coarse-level tiles we already have, scaled to the
    // minimap. Each tile covers its scaled sub-rect of the minimap.
    for (let ty = 0; ty < smallest.tilesPerCol; ty++) {
      for (let tx = 0; tx < smallest.tilesPerRow; tx++) {
        const t = cachedTile(smallest, tx, ty);
        if (!t) {
          // Kick off the load so the minimap paints in soon — but
          // skip tiles we already tried and failed, otherwise every
          // redraw re-enqueues them (storm). Sticky: minimap tiles
          // never get LRU-evicted from the main cache, so panning the
          // main viewport can't thrash them out (pre-fix, every evict
          // triggered a refetch on the next rAF).
          if (!skipMinimapFetch && !tileIsSettled(smallest, tx, ty)) {
            getTile(pp, smallest, tx, ty, { sticky: true })
              .then(() => redraw()).catch(() => {});
          }
          continue;
        }
        const destX = Math.round(tx * smallest.meta.cols / smallest.meta.totalCols * W);
        const destY = Math.round(ty * smallest.meta.rows / smallest.meta.totalRows * H);
        const destW = Math.ceil(smallest.meta.cols / smallest.meta.totalCols * W);
        const destH = Math.ceil(smallest.meta.rows / smallest.meta.totalRows * H);
        ctx.drawImage(t.image, destX, destY, destW, destH);
      }
    }

    // Viewport rectangle.
    const z = zoom();
    const viewW = mainCvs.clientWidth  / z;
    const viewH = mainCvs.clientHeight / z;
    const x0 = panX() - viewW / 2;
    const y0 = panY() - viewH / 2;
    ctx.strokeStyle = "#f85149";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      Math.max(1, Math.round(x0 * W / slideWidth)),
      Math.max(1, Math.round(y0 * H / slideHeight)),
      Math.max(2, Math.round(viewW * W / slideWidth)),
      Math.max(2, Math.round(viewH * H / slideHeight)),
    );
  }

  // One rAF at a time. `scheduleDraw` coalesces many calls within a
  // single frame into one `draw()` — cheap to call from anywhere,
  // safe from stack-overflow because it never calls itself
  // synchronously.
  let rafId = 0;
  function scheduleDraw() {
    if (rafId) return;
    rafId = requestAnimationFrame(() => { rafId = 0; draw(); });
  }
  const redraw = scheduleDraw;
  createEffect(() => { zoom(); panX(); panY(); pyr(); scheduleDraw(); });

  // Fit-on-pyramid-ready. Also fires when autoFit flips on (e.g. the
  // user hit "fit" to re-enable it). No rAF polling — the
  // ResizeObserver below handles the "canvas hasn't been measured
  // yet" case by calling fitToViewport again as soon as the
  // container has a size.
  createEffect(() => { pyr(); if (autoFit()) fitToViewport(); });

  // Expose a view-setter handle to the parent shell so snapshot
  // thumbnails can "click to restore" the exact pan/zoom that was
  // captured. Plain setters wired to the existing signals — no
  // dragging or animation, just jump. The parent stashes this
  // handle via `onViewportReady` on mount.
  function setView(v: { panX: number; panY: number; zoom: number }) {
    setAutoFit(false);
    setPanX(v.panX);
    setPanY(v.panY);
    setZoom(v.zoom);
    scheduleDraw();
  }
  function getView() {
    return { panX: panX(), panY: panY(), zoom: zoom() };
  }

  /**
   * Composite the rendered WSI canvas with the annotation SVG
   * overlay and return a JPEG data URL. Used by the "snapshot for
   * report" button.
   *
   *   1. Copy mainCvs pixel-for-pixel onto an offscreen canvas.
   *   2. Serialise overlaySvg to a data URL; load as Image; drawImage.
   *   3. toDataURL('image/jpeg', 0.85) for compact PDF embedding.
   *
   * The SVG rasterisation path is async because Image.onload fires
   * off the microtask queue. Returns a Promise<string | null>.
   */
  async function captureSnapshot(): Promise<string | null> {
    if (!mainCvs) return null;
    const w = mainCvs.width;
    const h = mainCvs.height;
    if (!w || !h) return null;
    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const ctx = off.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(mainCvs, 0, 0);
    // SVG overlay → raster via Blob URL. The viewport's SVG uses a
    // transformed <g> in image-space; when serialised with the same
    // clientWidth/clientHeight it paints at the same on-screen
    // pixel positions as the live overlay.
    if (overlaySvg) {
      try {
        const cloned = overlaySvg.cloneNode(true) as SVGSVGElement;
        // Preserve outer dimensions so the SVG rasterises at the
        // same size as the canvas.
        cloned.setAttribute("width", String(w));
        cloned.setAttribute("height", String(h));
        cloned.setAttribute("viewBox", `0 0 ${w} ${h}`);
        const xml = new XMLSerializer().serializeToString(cloned);
        const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.onload  = () => { ctx.drawImage(img, 0, 0, w, h); URL.revokeObjectURL(url); resolve(); };
          img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
          img.src = url;
        });
      } catch (e) {
        console.warn("[wsi/snapshot] svg compose failed", e);
      }
    }
    // Scale bar — the live scale bar is a DOM HUD (not part of
    // overlaySvg), so the SVG compose step misses it. Draw it
    // directly onto the snapshot canvas. Same geometry + style as
    // the on-screen version: 4 alternating black/white segments
    // with a µm/mm legend, or "≈" prefix when uncalibrated.
    try {
      const nice = pickNiceScale(120);
      const barPx = Math.max(20, Math.min(300, nice.pxOnScreen));
      // Scale factor from CSS px → canvas px (canvas may be
      // devicePixelRatio-scaled).
      const cssW = mainCvs.clientWidth || w;
      const s    = w / cssW;
      const margin   = 8 * s;
      const padX     = 8 * s;
      const padY     = 4 * s;
      const barH     = 8 * s;
      const fontPx   = 10 * s;
      const label    = `${mppCalibrated() ? "" : "≈ "}${formatMicron(nice.lengthUm)}`;
      const barWPx   = barPx * s;
      ctx.save();
      ctx.font = `600 ${fontPx}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      ctx.textBaseline = "top";
      const labelW = ctx.measureText(label).width;
      const boxW   = Math.max(barWPx, labelW) + padX * 2;
      const boxH   = barH + fontPx + padY * 3;
      const x0     = margin;
      const y0     = h - margin - boxH;
      // Background (semi-transparent, rounded 4 px)
      ctx.fillStyle = "rgba(0,0,0,0.72)";
      const r = 4 * s;
      ctx.beginPath();
      ctx.moveTo(x0 + r, y0);
      ctx.lineTo(x0 + boxW - r, y0);
      ctx.quadraticCurveTo(x0 + boxW, y0, x0 + boxW, y0 + r);
      ctx.lineTo(x0 + boxW, y0 + boxH - r);
      ctx.quadraticCurveTo(x0 + boxW, y0 + boxH, x0 + boxW - r, y0 + boxH);
      ctx.lineTo(x0 + r, y0 + boxH);
      ctx.quadraticCurveTo(x0, y0 + boxH, x0, y0 + boxH - r);
      ctx.lineTo(x0, y0 + r);
      ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
      ctx.closePath();
      ctx.fill();
      // Bar, centred in the box
      const bx = x0 + (boxW - barWPx) / 2;
      const by = y0 + padY;
      const segW = barWPx / 4;
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = (i % 2 === 0) ? "#fff" : "#000";
        ctx.fillRect(bx + i * segW, by, segW, barH);
      }
      ctx.strokeStyle = "rgba(255,255,255,0.85)";
      ctx.lineWidth = Math.max(1, s);
      ctx.strokeRect(bx, by, barWPx, barH);
      // Label
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.fillText(label, x0 + boxW / 2, by + barH + padY);
    } catch (e) { /* best-effort — scale bar is cosmetic */ }
    try { return off.toDataURL("image/jpeg", 0.85); }
    catch { return null; }
  }

  async function doSnapshot() {
    const dataUrl = await captureSnapshot();
    if (!dataUrl || !p.onSnapshot) return;
    p.onSnapshot({ dataUrl, panX: panX(), panY: panY(), zoom: zoom() });
  }

  onMount(() => {
    // Publish the view-control handle back to the parent shell so
    // snapshot thumbnails can jump the viewport to a stored view.
    p.onViewportReady?.({ setView, getView });

    // Single ResizeObserver on the viewport container. The canvas is
    // `position:absolute; inset:0` so its size derives from the
    // container — observing the container catches every layout change
    // (window resize, tray collapse, right dock collapse, AI panel
    // collapse) deterministically.
    const ro = new ResizeObserver(() => {
      if (autoFit()) fitToViewport();
      scheduleDraw();
    });
    if (container) ro.observe(container);
    scheduleDraw();
    onCleanup(() => ro.disconnect());

    // Keyboard shortcuts — HALO AP muscle memory.
    const kd = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "z") { e.preventDefault(); p.store.undo(); return; }
        if (e.key === "y" || (e.shiftKey && e.key === "Z")) { e.preventDefault(); p.store.redo(); return; }
      }
      if (e.key === "Escape") {
        // Also clear any in-flight pointer drag — a missed pointerup
        // (release outside the canvas, browser swallowed event during
        // a context-menu, …) otherwise leaves the cursor latched on
        // "grabbing" forever and blocks subsequent clicks. Esc is the
        // user's universal "get me out" key, so treat it as such.
        setStroke(null);
        setTool("move");
        dragging = null;
        pinch    = null;
        pointers.clear();
        clearLongPress();
        return;
      }
      if (e.key === "f" || e.key === "F") { setAutoFit(true); fitToViewport(); return; }
      if (e.key === "v" || e.key === "V") { setTool("move"); return; }
      if (e.key === "r" && !e.ctrlKey) { setTool("rect"); return; }
      if (e.key === "o") { setTool("ellipse"); return; }
      if (e.key === "p") { setTool("polygon"); return; }
      if (e.key === "g" || e.key === "G") { setTool("freehand"); return; }
      if (e.key === ".") { setTool("point"); return; }
      if (e.key === "l" || e.key === "L") { setTool("ruler"); return; }
      if (e.key === "t" || e.key === "T") { setTool("text"); return; }
      if ((e.key === "s" || e.key === "S") && !e.ctrlKey && !e.metaKey && p.onSnapshot) {
        // `S` captures current view — bare key to match QuPath /
        // HALO-AP muscle memory. Ctrl/⌘+S stays reserved for the
        // browser's "Save page" so we don't hijack the user's OS.
        doSnapshot();
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        const id = p.store.activeId();
        if (id) p.store.remove(id);
      }
      const n = parseInt(e.key, 10);
      if (Number.isFinite(n) && MAG_PRESETS.includes(n)) setMag(n);
    };
    window.addEventListener("keydown", kd);
    onCleanup(() => window.removeEventListener("keydown", kd));

    // Safety net: if the canvas-level onUp doesn't fire (button
    // released over a scrollbar, browser context menu intercepted,
    // dev-tools focus), the closure-local `dragging` stays set and
    // the cursor latches on "grabbing" forever. A window-level
    // pointerup catches every release; we only reset state when no
    // pointers remain so a one-finger-lifted-from-pinch transition
    // (handled by canvas onUp) isn't clobbered.
    const winUp = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pointers.size === 0) {
        if (dragging) { dragging = null; scheduleDraw(); }
        pinch = null;
        clearLongPress();
      }
    };
    window.addEventListener("pointerup", winUp);
    window.addEventListener("pointercancel", winUp);
    onCleanup(() => {
      window.removeEventListener("pointerup", winUp);
      window.removeEventListener("pointercancel", winUp);
    });

    // Headless-test probe. Playwright reads these to assert that
    // pinch-zoom / preset taps actually mutated the zoom signal.
    // Cheap to expose and only useful to our own verify harness.
    (window as any).__zoomSnap = () => zoom();
    (window as any).__magSnap = () => currentMag();
    onCleanup(() => {
      delete (window as any).__zoomSnap;
      delete (window as any).__magSnap;
    });
  });

  // ---------- Slide-space coord helpers ----------
  function imgAt(e: PointerEvent | MouseEvent): { x: number; y: number } {
    const rect = mainCvs.getBoundingClientRect();
    const mx = (e as any).clientX - rect.left;
    const my = (e as any).clientY - rect.top;
    return {
      x: panX() + (mx - rect.width  / 2) / zoom(),
      y: panY() + (my - rect.height / 2) / zoom(),
    };
  }

  // ---------- Pointer ----------
  //
  // Single-pointer path drives pan + annotation drawing (unchanged
  // desktop behaviour). Two or more active pointers engage pinch-zoom
  // with a one-finger-pan component (mid-point translation), mirroring
  // how every mobile map/photo viewer feels. Annotation tools stay
  // single-finger so a stray second touch doesn't wreck a polygon.
  //
  // Touch-only niceties layered on top of the pan machinery:
  //
  //   double-tap  (same touch point, <300 ms, <16 px movement)
  //     → setAutoFit(true) + fitToViewport() — matches mouse dblclick
  //   long-press  (600 ms hold, <10 px movement, pan/move tool only)
  //     → prioritizeTiles([{tx,ty}]) under the finger — the mobile
  //        equivalent of "click to force reload" from the desktop UX
  //
  // Gesture timers are cleared on any subsequent pointer event so they
  // never fire after the user has already moved on.
  let dragging: null | { mode: "pan" | "draw"; x: number; y: number; x0: number; y0: number; moved: boolean } = null;
  // Active pointers keyed by pointerId. `pinch` is non-null while ≥2
  // pointers are down; it captures the starting anchor for stable
  // zoom math (distance and mid-point at gesture start, plus the
  // slide-space image point under the mid so the zoom feels "centered
  // on where the fingers are").
  const pointers = new Map<number, { x: number; y: number }>();
  let pinch: null | {
    startDist: number; startZoom: number;
    startMidX: number; startMidY: number;
    slideMidX: number; slideMidY: number;
  } = null;
  // Double-tap + long-press state.
  let lastTapAt = 0;
  let lastTapX = 0;
  let lastTapY = 0;
  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  let longPressStart: { x: number; y: number; img: { x: number; y: number } } | null = null;
  const DOUBLE_TAP_MS = 300;
  const DOUBLE_TAP_TOL = 16;
  const LONG_PRESS_MS = 600;
  const LONG_PRESS_TOL = 10;

  function clearLongPress() {
    if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
    longPressStart = null;
  }
  function armLongPress(img: { x: number; y: number }, x: number, y: number) {
    clearLongPress();
    longPressStart = { x, y, img };
    longPressTimer = setTimeout(() => {
      longPressTimer = null;
      const pp = pyr();
      if (!pp || !longPressStart) return;
      const level = pickLevel(pp, zoom());
      const tx = Math.floor(longPressStart.img.x * level.scale / level.meta.cols);
      const ty = Math.floor(longPressStart.img.y * level.scale / level.meta.rows);
      if (tx < 0 || ty < 0 || tx >= level.tilesPerRow || ty >= level.tilesPerCol) return;
      prioritizeTiles(pp, level, [{ tx, ty }]).then(() => redraw()).catch(() => {});
    }, LONG_PRESS_MS);
  }

  function onDown(e: PointerEvent) {
    mainCvs.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Second pointer → engage pinch. Drop any single-pointer drag
    // state so the ensuing onMove doesn't try to pan the canvas
    // simultaneously; panning during pinch is handled by the
    // mid-point translation below.
    if (pointers.size >= 2) {
      clearLongPress();
      const pts = [...pointers.values()];
      const dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
      const startDist = Math.max(1, Math.hypot(dx, dy));
      const startMidX = (pts[0].x + pts[1].x) / 2;
      const startMidY = (pts[0].y + pts[1].y) / 2;
      const rect = mainCvs.getBoundingClientRect();
      const slideMidX = panX() + (startMidX - rect.left - rect.width  / 2) / zoom();
      const slideMidY = panY() + (startMidY - rect.top  - rect.height / 2) / zoom();
      pinch = { startDist, startZoom: zoom(), startMidX, startMidY, slideMidX, slideMidY };
      dragging = null;
      setAutoFit(false);
      cancelBackground();
      e.preventDefault();
      return;
    }

    const t = tool();
    const img = imgAt(e);
    const wantPan = t === "move" || e.button === 2 || (e as any).shiftKey;
    cancelBackground();        // foreground interaction: pause bg loader
    if (wantPan) {
      setAutoFit(false);
      dragging = { mode: "pan", x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, moved: false };
      armLongPress(img, e.clientX, e.clientY);
      e.preventDefault();
      return;
    }
    if (t === "rect" || t === "ellipse" || t === "ruler") {
      setStroke({ kind: "drag2", a: img, b: img });
    } else if (t === "freehand") {
      setStroke({ kind: "freehand", pts: [img] });
    } else if (t === "polygon") {
      const s = stroke();
      if (!s || s.kind !== "polygon") setStroke({ kind: "polygon", pts: [img], live: img });
      else setStroke({ kind: "polygon", pts: [...s.pts, img], live: img });
    } else if (t === "point") {
      p.store.add(mkBase("point", p.activeClass, { at: img })); setStroke(null);
    } else if (t === "text") {
      const txt = window.prompt("Annotation text:");
      if (txt && txt.trim()) p.store.add(mkBase("text", p.activeClass, { at: img, text: txt.trim() }));
      setStroke(null);
    }
    dragging = { mode: "draw", x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, moved: false };
    e.preventDefault();
  }
  function onMove(e: PointerEvent) {
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Pinch-zoom path. Update zoom from the ratio of current finger
    // distance to the starting distance. Pan from the translation of
    // the mid-point so the slide tracks the fingers as they drift
    // across the screen. Both updates come through the existing
    // setZoom/setPanX/setPanY signals so the rAF redraw + minimap +
    // annotation overlay stay in sync without new code paths.
    if (pinch && pointers.size >= 2) {
      const pts = [...pointers.values()];
      const dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
      const dist = Math.max(1, Math.hypot(dx, dy));
      const midX = (pts[0].x + pts[1].x) / 2;
      const midY = (pts[0].y + pts[1].y) / 2;
      const zNext = Math.max(0.02, Math.min(40, pinch.startZoom * (dist / pinch.startDist)));
      const rect = mainCvs.getBoundingClientRect();
      setZoom(zNext);
      // Keep the slide point that was under the starting mid-point
      // pinned to the CURRENT mid-point, so the gesture feels
      // anchored at the fingers.
      setPanX(pinch.slideMidX - (midX - rect.left - rect.width  / 2) / zNext);
      setPanY(pinch.slideMidY - (midY - rect.top  - rect.height / 2) / zNext);
      e.preventDefault();
      return;
    }

    const img = imgAt(e);
    setCursorX(img.x); setCursorY(img.y);

    // Long-press cancel — any pointer movement past the tolerance
    // invalidates the pending long-press regardless of whether we're
    // dragging (mouse buttons up) or just moving (polygon hover).
    if (longPressTimer && longPressStart) {
      const ldx = e.clientX - longPressStart.x, ldy = e.clientY - longPressStart.y;
      if (ldx * ldx + ldy * ldy > LONG_PRESS_TOL * LONG_PRESS_TOL) clearLongPress();
    }

    // Drag-sentinel updates only matter when a pointer is actually
    // down. For the pure "hover to update polygon live vertex" case
    // there IS no dragging object — fall through to stroke state
    // below so polygon's floating last vertex tracks the cursor
    // between clicks, matching Radiology's unconditional liveImg
    // pattern in `viewport/AnnotationLayer.tsx`.
    if (dragging) {
      const dxTotal = e.clientX - dragging.x0, dyTotal = e.clientY - dragging.y0;
      if (!dragging.moved && dxTotal * dxTotal + dyTotal * dyTotal > 4 * 4) {
        dragging.moved = true;       // >4px: actual drag, not a click
      }
      if (dragging.mode === "pan") {
        const dx = e.clientX - dragging.x, dy = e.clientY - dragging.y;
        dragging.x = e.clientX; dragging.y = e.clientY;
        setPanX((x) => x - dx / zoom());
        setPanY((y) => y - dy / zoom());
        return;
      }
    }

    // Stroke state updates — not gated on `dragging` for drag2 and
    // freehand it may look like these only matter during a drag,
    // but gating on `dragging` was dropping polygon's hover updates
    // between clicks (live vertex froze on the previous click). The
    // per-kind checks below keep drag2/freehand safe (they only
    // fire while a stroke is live, which onDown/onUp already gate).
    const s = stroke();
    if (!s) return;
    if (s.kind === "drag2") {
      // Only extend drag2 while the pointer is actually down —
      // dragging === "draw" tells us we're inside a live gesture.
      if (dragging?.mode === "draw") setStroke({ ...s, b: img });
    } else if (s.kind === "freehand") {
      if (dragging?.mode === "draw") setStroke({ ...s, pts: [...s.pts, img] });
    } else if (s.kind === "polygon") {
      // Polygon is multi-click: we want the last segment to track the
      // cursor EVEN between clicks, i.e. with no active drag.
      setStroke({ ...s, live: img });
    }
  }
  function onUp(e: PointerEvent) {
    try { mainCvs.releasePointerCapture(e.pointerId); } catch {}
    pointers.delete(e.pointerId);

    // Coming out of pinch: if one finger stays down, seed a new pan
    // drag from it rather than leaving the gesture half-alive.
    if (pinch) {
      if (pointers.size < 2) {
        pinch = null;
        if (pointers.size === 1) {
          const [remaining] = pointers.values();
          dragging = { mode: "pan", x: remaining.x, y: remaining.y, x0: remaining.x, y0: remaining.y, moved: true };
        } else {
          dragging = null;
        }
      }
      return;
    }

    clearLongPress();

    if (!dragging) return;
    // Bare-click in pan mode (no drag) on an un-rendered tile → jump
    // that tile to the head of the queue. Matches Aperio/Slim's
    // "click to force reload" UX for the occasional tile that 500'd
    // or got aborted. Pan drags (moved=true) fall through.
    if (dragging.mode === "pan" && !dragging.moved) {
      // Touch-level double-tap: same point, <300 ms since last tap →
      // fit-to-view, matching the mouse onDblClick behaviour for
      // pointer types (like "touch") that don't synthesise dblclick.
      const now = Date.now();
      const dx = e.clientX - lastTapX, dy = e.clientY - lastTapY;
      const isTouchPointer = e.pointerType === "touch" || e.pointerType === "pen";
      if (isTouchPointer &&
          now - lastTapAt < DOUBLE_TAP_MS &&
          dx * dx + dy * dy < DOUBLE_TAP_TOL * DOUBLE_TAP_TOL) {
        setAutoFit(true);
        fitToViewport();
        lastTapAt = 0;             // consumed — don't chain a triple-tap
        dragging = null;
        return;
      }
      lastTapAt = now;
      lastTapX = e.clientX;
      lastTapY = e.clientY;

      const pp = pyr();
      if (pp) {
        const img = imgAt(e);
        const level = pickLevel(pp, zoom());
        const tx = Math.floor(img.x * level.scale / level.meta.cols);
        const ty = Math.floor(img.y * level.scale / level.meta.rows);
        if (tx >= 0 && ty >= 0 && tx < level.tilesPerRow && ty < level.tilesPerCol) {
          const existing = cachedTile(level, tx, ty);
          if (!existing) {
            prioritizeTiles(pp, level, [{ tx, ty }])
              .then(() => redraw())
              .catch(() => { /* already latched as failed again */ });
          }
        }
      }
    }
    if (dragging.mode === "draw") {
      const s = stroke();
      if (s && s.kind === "drag2") {
        if (tool() === "rect")    p.store.add(mkBase("rect",    p.activeClass, { a: s.a, b: s.b }));
        else if (tool() === "ellipse") p.store.add(mkBase("ellipse", p.activeClass, { a: s.a, b: s.b }));
        else if (tool() === "ruler")   p.store.add(mkBase("ruler",   p.activeClass, { a: s.a, b: s.b }));
        setStroke(null);
      } else if (s && s.kind === "freehand" && s.pts.length >= 3) {
        p.store.add(mkBase("freehand", p.activeClass, { points: s.pts }));
        setStroke(null);
      }
    }
    dragging = null;
  }
  function onDblClick(e: MouseEvent) {
    const s = stroke();
    if (s?.kind === "polygon" && s.pts.length >= 3) {
      p.store.add(mkBase("polygon", p.activeClass, { points: s.pts }));
      setStroke(null);
      return;
    }
    // Double-click always snaps back to fit-all-and-follow-layout mode.
    setAutoFit(true);
    fitToViewport();
    e.preventDefault();
  }
  function onWheel(e: WheelEvent) {
    e.preventDefault();
    setAutoFit(false);
    cancelBackground();          // foreground zoom: pause bg loader
    const rect = mainCvs.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const z0 = zoom();
    const ix = panX() + (mx - rect.width  / 2) / z0;
    const iy = panY() + (my - rect.height / 2) / z0;
    const factor = e.deltaY < 0
      ? (e.ctrlKey ? 1.3 : e.shiftKey ? 1.05 : 1.15)
      : (e.ctrlKey ? 1 / 1.3 : e.shiftKey ? 1 / 1.05 : 1 / 1.15);
    const z1 = Math.max(0.02, Math.min(40, z0 * factor));
    setZoom(z1);
    setPanX(ix - (mx - rect.width  / 2) / z1);
    setPanY(iy - (my - rect.height / 2) / z1);
  }
  function onMinimap(e: MouseEvent) {
    const pp = pyr();
    if (!pp) return;
    setAutoFit(false);
    const rect = miniCvs.getBoundingClientRect();
    setPanX(((e.clientX - rect.left) / rect.width)  * pp.slideWidth);
    setPanY(((e.clientY - rect.top)  / rect.height) * pp.slideHeight);
  }

  // ---------- Magnification ----------
  //
  // In clinical pathology "×N" is OBJECTIVE-EQUIVALENT, derived from
  // pixel spacing (MPP = μm / pixel), not a fixed 40× assumption:
  //
  //   mag_objective_eq = (10 μm / MPP_base) × zoom
  //
  // …where `zoom` is screen-pixels per base-level-pixel (1.0 = one
  // slide pixel per screen pixel). Example: a slide scanned at 0.25
  // MPP shows 40× at zoom=1, 100× at zoom=2.5 (digital). If the
  // source DICOM didn't carry ImagedVolumeWidth / PixelSpacing, we
  // fall back to a 1 MPP estimate (i.e. native = 10×), which is the
  // pessimistic case — a clinical deployment would require the
  // scanner to populate the physical scale tags per DICOM WG-26.
  //
  // "Beyond native" presets (100× in our ladder on most scans) are
  // legitimate — every commercial pathology viewer supports digital
  // zoom — but are labelled with "(digital)" suffix so the reader
  // knows the extra magnification is upsampling, not new detail.
  const FALLBACK_MPP = 1.0;
  const nativeMpp = () => pyr()?.mppX0 ?? FALLBACK_MPP;
  const nativeMag = () => 10.0 / nativeMpp();   // objective-eq at zoom = 1
  const currentMag = () => nativeMag() * zoom();
  const isDigitalZoom = () => zoom() > 1.0;
  /** Whether the source DICOM actually carried physical pixel spacing.
   *  When false we can still render a scale bar but mark it as
   *  uncalibrated — an un-authenticated guess (FALLBACK_MPP=1µm/px).
   *  Pathologists should not trust micrometer labels on an
   *  uncalibrated slide. */
  const mppCalibrated = () => !!pyr()?.mppX0;
  /** Format a length in micrometres into the reader-friendly unit —
   *  µm for sub-millimetre, mm with 1-3 sig figs for larger.
   *  Matches the convention used in QuPath / HALO AP. */
  function formatMicron(um: number): string {
    const a = Math.abs(um);
    if (a < 1)     return `${um.toFixed(2)} µm`;
    if (a < 1000)  return `${um.toFixed(a < 10 ? 1 : 0)} µm`;
    const mm = um / 1000;
    return `${mm < 10 ? mm.toFixed(2) : mm < 100 ? mm.toFixed(1) : mm.toFixed(0)} mm`;
  }
  /** Choose a "nice" scale-bar length (1, 2 or 5 × 10^n µm) whose
   *  on-screen extent is close to `targetPx` screen pixels.
   *  Returns { lengthUm, pxOnScreen } so the caller can render the
   *  bar at the correct width. */
  function pickNiceScale(targetPx: number): { lengthUm: number; pxOnScreen: number } {
    const pxPerUm = zoom() / nativeMpp();        // screen-px per µm
    if (pxPerUm <= 0 || !isFinite(pxPerUm)) return { lengthUm: 1, pxOnScreen: targetPx };
    const rawUm = targetPx / pxPerUm;
    // Snap to 1/2/5 × 10^n.
    const exp = Math.floor(Math.log10(rawUm));
    const base = Math.pow(10, exp);
    const r = rawUm / base;
    const niceMantissa = r >= 5 ? 5 : r >= 2 ? 2 : 1;
    const lengthUm = niceMantissa * base;
    const pxOnScreen = lengthUm * pxPerUm;
    return { lengthUm, pxOnScreen };
  }
  function setMag(x: number) {
    if (!isFinite(x) || x <= 0) return;
    // Any explicit magnification choice leaves auto-fit mode — without
    // this the next ResizeObserver tick calls fitToViewport() and
    // silently undoes the zoom, which looked from the user's seat
    // like "5×/10×/20× don't do anything".
    setAutoFit(false);
    // zoom = desired_objective / native_objective. `nativeMag()` pulls
    // from the base instance's MPP so this works correctly regardless
    // of the scanner's native resolution.
    setZoom(x / nativeMag());
  }
  function submitMag(e: Event) {
    e.preventDefault();
    const v = parseFloat(magInput());
    if (isFinite(v)) setMag(v);
    setMagInput("");
  }

  // ---------- Annotation overlay (SVG) ----------
  const transformAttr = () => {
    if (!mainCvs || !pyr()) return "";
    const w = mainCvs.clientWidth, h = mainCvs.clientHeight;
    const z = zoom();
    const tx = w / 2 - panX() * z;
    const ty = h / 2 - panY() * z;
    return `translate(${tx} ${ty}) scale(${z})`;
  };

  return (
    <div ref={container} class="relative flex h-full w-full flex-col bg-ink-0">
      {/* Top bar — horizontal scroll on phones so the whole preset
          ladder stays reachable without wrapping into a double row
          that would eat 40+ px of vertical canvas. Desktop keeps its
          flex row; `md:overflow-visible` unwinds the scroll container
          at ≥768 px so the absolutely-positioned autofill inside the
          minimap etc. is unaffected.
          Hidden in fullscreen mode so the reader sees nothing but
          slide + minimap. */}
      <Show when={!p.fullscreen}>
      <div class="flex items-center gap-2 overflow-x-auto whitespace-nowrap border-b border-ink-300 bg-ink-100 px-3 py-1.5 text-[11px] md:overflow-x-visible md:whitespace-normal">
        <span class="chip-brand !text-[9px]" title={`build ${__BUILD_ID__}`}>
          WSI · H&amp;E / IHC · <span class="opacity-60 font-mono">{__BUILD_ID__}</span>
        </span>
        <Show when={p.slideLabel}>
          <span class="truncate text-ink-900">{p.slideLabel}</span>
        </Show>
        <Show when={pyr()}>
          <span class="text-[10px] text-ink-700">
            pyramid: <span class="font-mono text-ink-900">{pyr()!.levels.length}</span> lvl ·
            base <span class="font-mono text-ink-900">{pyr()!.slideWidth}×{pyr()!.slideHeight}</span>
          </span>
        </Show>
        <span class="ml-auto text-ink-700">
          mag:
          <Show when={nativeMpp() && pyr()}>
            <span class="ml-1 text-[9px] text-ink-700">
              ({nativeMpp().toFixed(3)} μm/px · native {nativeMag().toFixed(0)}×)
            </span>
          </Show>
          {/* Active pyramid level — derived from current zoom. Shows
              WHICH DICOM instance is actually being decoded, so a
              pathologist knows whether they're looking at a native
              level's own pixel data (native XX× from the level's own
              mpp) or digital zoom beyond the finest native resolution. */}
          <Show when={pyr() && mainCvs}>
            {(() => {
              const pp = pyr()!;
              const lvl = pickLevel(pp, zoom());
              const lvlIdx = pp.levels.indexOf(lvl);
              const lvlNativeMag = (lvl.meta.mppX && lvl.meta.mppX > 0) ? 10.0 / lvl.meta.mppX : nativeMag() * lvl.scale;
              const digital = zoom() > lvl.scale * 1.05;
              return (
                <span class="ml-2 text-[9px] font-mono"
                      classList={{
                        "text-brand": !digital,
                        "text-ink-700 italic": digital,
                      }}
                      title={digital
                        ? `digital zoom (upsampling the ${lvlNativeMag.toFixed(0)}× native level)`
                        : `native ${lvlNativeMag.toFixed(0)}× DICOM instance`}>
                  [lvl {lvlIdx + 1}/{pp.levels.length} · {digital ? "digital" : "native"} {lvlNativeMag.toFixed(0)}×]
                </span>
              );
            })()}
          </Show>
        </span>
        <form onSubmit={submitMag}>
          <input type="text"
                 class="w-[5.5rem] rounded border border-ink-300 bg-ink-200 px-1.5 py-0.5 text-right font-mono text-[11px] text-ink-900 focus:border-brand focus:outline-none"
                 placeholder={currentMag().toFixed(1) + "×"}
                 classList={{ "!text-ink-700 italic": isDigitalZoom() }}
                 value={magInput()}
                 onInput={(e) => setMagInput((e.currentTarget as HTMLInputElement).value)} />
        </form>
        {/* Text mag chips removed — replaced by the always-visible
            objective picker overlay further down so the control
            survives fullscreen and uses the custom microscope-lens
            icon design. */}
        <button type="button" class="btn-ghost !text-[10px]"
                title="Fit whole slide (F / double-click)"
                onClick={() => { setAutoFit(true); fitToViewport(); }}>
          fit
        </button>
      </div>
      </Show>

      <div class="relative flex-1 overflow-hidden">
        <canvas ref={mainCvs}
                class="absolute inset-0 h-full w-full touch-none [overscroll-behavior:contain]"
                classList={{
                  "cursor-grab":      tool() === "move" && !dragging,
                  "cursor-grabbing":  dragging?.mode === "pan",
                  "cursor-crosshair": tool() !== "move",
                }}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
                onWheel={onWheel}
                onDblClick={onDblClick}
                onContextMenu={(e) => e.preventDefault()} />

        {/* Loading indicator — appears while tiles are inflight
            (either from the user's interaction, the progressive bg
            loader, or a priority re-fetch) OR when the partial-render
            fallback is active. Aria-live so screen readers pick up
            the state change. */}
        <Show when={inflight() > 0 || loadingStats().missingInView > 0 || loadingStats().partialRender}>
          {/* Bottom-centre pill — the previous top-left placement
              sat under the cursor-coordinates HUD and was effectively
              invisible. Bottom-centre is the standard pathology-
              viewer location (HALO, Slim, Sectra) for "tiles loading"
              status and reads cleanly against any slide. */}
          <div class="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full bg-ink-100/95 px-3 py-1.5 text-xs font-medium text-ink-900 shadow-md backdrop-blur ring-1 ring-ink-300"
               aria-live="polite"
               role="status">
            <span class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-brand border-t-transparent" />
            <span>
              {loadingStats().partialRender
                ? `Processing ${loadingStats().totalVisible.toLocaleString()} tiles (showing ${loadingStats().visibleCount} nearest)…`
                : inflight() > 0
                  ? `Loading ${inflight()} tile${inflight() === 1 ? "" : "s"}…`
                  : "Rendering…"}
            </span>
          </div>
        </Show>

        {/* SVG annotation overlay */}
        <svg ref={overlaySvg} class="pointer-events-none absolute inset-0 h-full w-full">
          <g transform={transformAttr()}>
            <For each={p.store.state().items}>{(a) => (
              <AnnoShape a={a}
                         active={p.store.activeId() === a.id}
                         strokePx={1.6 / Math.max(zoom(), 0.01)}
                         mppX={nativeMpp()}
                         calibrated={mppCalibrated()} />
            )}</For>
            {/* Ghost preview — reactive inline expressions.
                Solid's <Show> is NOT keyed by default: the child
                function runs ONCE when when-becomes-truthy and
                subsequent stroke() updates don't re-run it. Capturing
                `const sv = s()` then computing rect x/y/w/h from it
                froze the ghost at 0×0 (the pointerdown seed where
                a === b). Two fixes applied below:
                  1. `<Show keyed>` so the child runs on every new
                     stroke reference (pointermove sets a fresh
                     object each time).
                  2. Even so, we read through createMemo-style inline
                     expressions in the SVG attrs to be defensive
                     against any future change to Show's behaviour. */}
            <Show when={stroke()} keyed>
              {(sv) => {
                const col = p.activeClass.color;
                const invZ = () => 1 / Math.max(zoom(), 0.01);
                const sw     = () => 1.6 * invZ();
                const haloSw = () => 4.0 * invZ();
                const dash   = () => `${6 * invZ()} ${4 * invZ()}`;
                const fillTint = col + "18";

                if (sv.kind === "drag2") {
                  if (tool() === "rect") {
                    const x = () => Math.min(sv.a.x, sv.b.x);
                    const y = () => Math.min(sv.a.y, sv.b.y);
                    const w = () => Math.abs(sv.b.x - sv.a.x);
                    const h = () => Math.abs(sv.b.y - sv.a.y);
                    return <>
                      <rect x={x()} y={y()} width={w()} height={h()}
                            fill={fillTint}
                            stroke="rgba(2, 6, 23, 0.85)" stroke-width={haloSw()} />
                      <rect x={x()} y={y()} width={w()} height={h()}
                            fill="none"
                            stroke={col} stroke-width={sw()} stroke-dasharray={dash()} />
                    </>;
                  }
                  if (tool() === "ellipse") {
                    const cx = () => (sv.a.x + sv.b.x) / 2;
                    const cy = () => (sv.a.y + sv.b.y) / 2;
                    const rx = () => Math.abs(sv.b.x - sv.a.x) / 2;
                    const ry = () => Math.abs(sv.b.y - sv.a.y) / 2;
                    return <>
                      <ellipse cx={cx()} cy={cy()} rx={rx()} ry={ry()}
                               fill={fillTint}
                               stroke="rgba(2, 6, 23, 0.85)" stroke-width={haloSw()} />
                      <ellipse cx={cx()} cy={cy()} rx={rx()} ry={ry()}
                               fill="none"
                               stroke={col} stroke-width={sw()} stroke-dasharray={dash()} />
                    </>;
                  }
                  if (tool() === "ruler") {
                    const distUm = () => Math.hypot(sv.b.x - sv.a.x, sv.b.y - sv.a.y) * nativeMpp();
                    const mx = () => (sv.a.x + sv.b.x) / 2;
                    const my = () => (sv.a.y + sv.b.y) / 2 - 8 * invZ();
                    return <>
                      <line x1={sv.a.x} y1={sv.a.y} x2={sv.b.x} y2={sv.b.y}
                            stroke="rgba(2, 6, 23, 0.85)" stroke-width={haloSw()} />
                      <line x1={sv.a.x} y1={sv.a.y} x2={sv.b.x} y2={sv.b.y}
                            stroke={col} stroke-width={sw()} />
                      <circle cx={sv.a.x} cy={sv.a.y} r={3 * invZ()} fill={col}
                              stroke="rgba(2,6,23,0.85)" stroke-width={1 * invZ()} />
                      <circle cx={sv.b.x} cy={sv.b.y} r={3 * invZ()} fill={col}
                              stroke="rgba(2,6,23,0.85)" stroke-width={1 * invZ()} />
                      <LabelHalo x={mx()} y={my()} text={formatMicron(distUm())} scale={invZ()} />
                    </>;
                  }
                } else if (sv.kind === "freehand" && sv.pts.length > 1) {
                  const pts = sv.pts.map((q) => `${q.x},${q.y}`).join(" ");
                  return <>
                    <polyline points={pts} fill="none"
                              stroke="rgba(2, 6, 23, 0.85)" stroke-width={haloSw()} />
                    <polyline points={pts} fill="none"
                              stroke={col} stroke-width={sw()} />
                  </>;
                } else if (sv.kind === "polygon") {
                  const live = [...sv.pts, sv.live].map((q) => `${q.x},${q.y}`).join(" ");
                  return <>
                    <polyline points={live} fill="none"
                              stroke="rgba(2, 6, 23, 0.85)" stroke-width={haloSw()} />
                    <polyline points={live} fill="none"
                              stroke={col} stroke-width={sw()} stroke-dasharray={dash()} />
                  </>;
                }
                return null;
              }}
            </Show>
          </g>
        </svg>

        {/* Annotation tool rail.
            Normal mode: vertical toolbar pinned to the right edge.
            Fullscreen: becomes a floating draggable card with a
            title-strip grip (same pattern as the Minimap) so the
            reader can reposition it wherever it stays out of their
            way. Collapses to a single "⋮" button in both modes. */}
        <Show when={railOpen()} fallback={
          <button type="button"
                  class="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded border border-ink-300 bg-black/70 px-1.5 py-2 text-[11px] text-white/80 backdrop-blur hover:bg-ink-200 hover:text-white"
                  title="Show annotation tools"
                  onClick={() => setRailOpen(true)}>⋮</button>
        }>
          <div ref={toolRailEl}
               class="absolute z-10 flex flex-col gap-1 rounded border border-ink-300 bg-black/75 p-1 text-[10px] text-ink-700 backdrop-blur shadow-lg"
               classList={{
                 // Normal mode: right-edge, vertically centred,
                 // 11-unit wide. Fullscreen with no user drag yet:
                 // same right-edge default. Dragged: absolute pos.
                 "right-1 top-1/2 -translate-y-1/2 w-11": !p.fullscreen && !toolPos(),
                 "w-11": !toolPos(),
               }}
               style={toolPos()
                 ? { left: `${toolPos()!.x}px`, top: `${toolPos()!.y}px`, width: "44px" }
                 : (p.fullscreen
                     ? { right: "12px", top: "50%", transform: "translateY(-50%)", width: "44px" }
                     : undefined)}>
            {/* Grip strip — only in fullscreen. In normal mode the
                rail isn't meant to move (the outer panels already
                have layout). */}
            <Show when={p.fullscreen}>
              <div class="flex cursor-grab items-center justify-between px-0.5 pb-0.5 text-[9px] uppercase tracking-wider text-white/60 active:cursor-grabbing"
                   onPointerDown={onToolGripDown}
                   onPointerMove={onToolGripMove}
                   onPointerUp={onToolGripUp}
                   onPointerCancel={onToolGripUp}
                   title="Drag to move">
                <span>tools</span>
                <button type="button"
                        class="text-white/70 hover:text-white"
                        title="Hide tools"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setRailOpen(false)}>×</button>
              </div>
              <div class="h-px w-full bg-ink-300/50" />
            </Show>
            <Show when={!p.fullscreen}>
              <button type="button"
                      class="grid h-6 w-9 place-items-center rounded text-[12px] text-white/70 hover:bg-ink-300 hover:text-white"
                      title="Hide tools"
                      onClick={() => setRailOpen(false)}>×</button>
            </Show>
            <ToolBtn active={tool() === "move"}     onClick={() => setTool("move")}     k="V" label="Pan">↔</ToolBtn>
            <ToolBtn active={tool() === "rect"}     onClick={() => setTool("rect")}     k="R" label="Rect">▭</ToolBtn>
            <ToolBtn active={tool() === "ellipse"}  onClick={() => setTool("ellipse")}  k="O" label="Ellipse">◯</ToolBtn>
            <ToolBtn active={tool() === "polygon"}  onClick={() => setTool("polygon")}  k="P" label="Polygon">⬠</ToolBtn>
            <ToolBtn active={tool() === "freehand"} onClick={() => setTool("freehand")} k="G" label="Freehand">∿</ToolBtn>
            <ToolBtn active={tool() === "point"}    onClick={() => setTool("point")}    k="." label="Point">●</ToolBtn>
            <ToolBtn active={tool() === "ruler"}    onClick={() => setTool("ruler")}    k="L" label="Ruler">╱</ToolBtn>
            <ToolBtn active={tool() === "text"}     onClick={() => setTool("text")}     k="T" label="Text">T</ToolBtn>
            <div class="my-0.5 h-px w-full bg-ink-300" />
            <Show when={p.onSnapshot}>
              <ToolBtn onClick={doSnapshot} k="S" label="Snapshot">📸</ToolBtn>
            </Show>
            <ToolBtn onClick={p.store.undo} k="⌃Z" label="Undo" disabled={!p.store.canUndo()}>↶</ToolBtn>
            <ToolBtn onClick={p.store.redo} k="⌃Y" label="Redo" disabled={!p.store.canRedo()}>↷</ToolBtn>
          </div>
        </Show>

        {/* Floating annotations quick-nav.
            Opens from a pill button on the left edge of the
            viewport. Content mirrors the right-dock AnnotationsTab
            but compact — thumbnail chip + label + click to jump.
            Only ROI annotations (not snapshots — those live on the
            Synoptic tab). Available in every mode so the reader
            can navigate to an ROI while typing in the Synoptic tab. */}
        <Show when={!annoNavOpen()}>
          <Show when={p.store.state().items.some((a) => a.kind !== "snapshot")}>
            <button type="button"
                    class="absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded border border-ink-300 bg-black/75 px-2 py-3 text-[10px] font-medium text-white/80 backdrop-blur hover:bg-ink-200 hover:text-white"
                    title="Show annotations quick-nav"
                    onClick={() => setAnnoNavOpen(true)}>
              ROIs<br />
              <span class="font-mono text-[9px] text-brand">
                {p.store.state().items.filter((a) => a.kind !== "snapshot").length}
              </span>
            </button>
          </Show>
        </Show>
        <Show when={annoNavOpen()}>
          <div ref={annoNavEl}
               class="absolute z-20 flex flex-col rounded border border-ink-300 bg-black/85 text-[11px] text-ink-900 shadow-lg backdrop-blur"
               style={annoNavPos()
                 ? { left: `${annoNavPos()!.x}px`, top: `${annoNavPos()!.y}px`, width: "280px" }
                 : { left: "12px", top: "80px", width: "280px" }}>
            <div class="flex cursor-grab items-center justify-between gap-2 border-b border-ink-300 px-2 py-1 text-white/80 active:cursor-grabbing"
                 onPointerDown={onAnnoNavGripDown}
                 onPointerMove={onAnnoNavGripMove}
                 onPointerUp={onAnnoNavGripUp}
                 onPointerCancel={onAnnoNavGripUp}
                 title="Drag to move">
              <span class="text-[9px] uppercase tracking-wider">annotations</span>
              <span class="font-mono text-[10px] text-brand">
                {p.store.state().items.filter((a) => a.kind !== "snapshot").length}
              </span>
              <button type="button"
                      class="ml-auto text-white/70 hover:text-white"
                      title="Hide"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => setAnnoNavOpen(false)}>×</button>
            </div>
            <div class="max-h-[420px] overflow-auto">
              <Show when={p.store.state().items.some((a) => a.kind !== "snapshot")} fallback={
                <div class="px-3 py-4 text-center text-[10px] text-ink-700">
                  No annotations yet.<br/>Draw one with R / O / P / G.
                </div>
              }>
                <Index each={p.store.state().items.filter((x) => x.kind !== "snapshot")}>{(aFn) => {
                  const a = aFn;
                  const isEditing = () => editingAnnoId() === a().id;
                  const isActive  = () => p.store.activeId()  === a().id;
                  return (
                    <div class="border-b border-ink-300"
                         classList={{ "bg-brand/10": isActive() }}>
                      {/* Row */}
                      <div class="flex items-center gap-1.5 px-2 py-1.5">
                        <button type="button"
                                class="flex min-w-0 flex-1 items-center gap-2 text-left"
                                title="Jump to this annotation"
                                onClick={() => {
                                  p.store.setActiveId(a().id);
                                  p.onNavigateTo?.(a());
                                }}>
                          <span class="h-2.5 w-2.5 shrink-0 rounded-sm ring-1 ring-black/30"
                                style={{ "background-color": a().color }} />
                          <span class="shrink-0 font-mono text-[9px] uppercase text-ink-700">{a().kind}</span>
                          <span class="truncate text-[11px] text-white">
                            {a().label && a().label!.length > 0 ? a().label : <span class="italic text-ink-700">unlabelled</span>}
                          </span>
                        </button>
                        <button type="button"
                                class="shrink-0 rounded p-1 text-ink-700 hover:bg-ink-200 hover:text-white"
                                classList={{ "text-brand": isEditing() }}
                                title="Edit label + class"
                                onClick={() => setEditingAnnoId(isEditing() ? null : a().id)}>
                          <IconEdit size={13} />
                        </button>
                        <button type="button"
                                class="shrink-0 rounded p-1 text-ink-700 hover:bg-ink-200 hover:text-danger"
                                title="Delete"
                                onClick={() => {
                                  if (editingAnnoId() === a().id) setEditingAnnoId(null);
                                  p.store.remove(a().id);
                                }}>
                          <IconTrash size={13} />
                        </button>
                      </div>
                      {/* Inline editor — Index keeps the input
                          node identity stable so typing doesn't
                          lose focus between keystrokes. */}
                      <Show when={isEditing()}>
                        <div class="space-y-1.5 border-t border-ink-300 px-2 py-1.5">
                          <input type="text"
                                 class="w-full rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                 placeholder="Annotation label"
                                 value={a().label ?? ""}
                                 onInput={(e) => p.store.replace(a().id, { label: (e.currentTarget as HTMLInputElement).value } as Partial<import("./annotations").PathAnno>)} />
                          <div class="flex flex-wrap gap-1">
                            <For each={p.classes ?? []}>{(c) => (
                              <button type="button"
                                      class="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]"
                                      classList={{
                                        "border-brand bg-brand/20 text-white": a().color === c.color,
                                        "border-ink-300 text-ink-800 hover:text-white": a().color !== c.color,
                                      }}
                                      title={`Set class to ${c.label}`}
                                      onClick={() => p.store.replace(a().id, { color: c.color, label: c.label } as Partial<import("./annotations").PathAnno>)}>
                                <span class="h-2 w-2 rounded-sm" style={{ "background-color": c.color }} />
                                {c.label}
                              </button>
                            )}</For>
                          </div>
                          <div class="flex justify-end">
                            <button type="button"
                                    class="inline-flex h-5 items-center rounded border border-brand bg-brand/15 px-2 text-[10px] text-brand hover:bg-brand hover:text-white"
                                    onClick={() => setEditingAnnoId(null)}>done</button>
                          </div>
                        </div>
                      </Show>
                    </div>
                  );
                }}</Index>
              </Show>
            </div>
          </div>
        </Show>
        <FullscreenToggle fullscreen={!!p.fullscreen} onClick={p.onToggleFullscreen} />

        {/* ===== Scale bar — persistent lower-left overlay =====
            Pathology + histology convention: show a segmented bar
            (alternating black / white like a standard ruler) with a
            µm or mm legend, snapped to a "nice" round value (1, 2,
            or 5 × 10^n) chosen to land close to a target 120-px
            length on screen. Updates reactively as the user zooms —
            the bar's pixel width stays between ~70 and ~140 px
            because the snap picks the next nicer value at every
            zoom threshold. Uncalibrated slides (no PixelSpacing in
            the source DICOM) get a "≈" prefix + dashed outline so
            the reader knows the label is a best-guess. */}
        <Show when={pyr()}>
          {(() => {
            const nice = pickNiceScale(120);
            const barPx = Math.max(20, Math.min(300, nice.pxOnScreen));
            return (
              <div class="pointer-events-none absolute bottom-2 left-2 z-20 select-none">
                <div class="flex flex-col items-center gap-0.5 rounded bg-black/70 px-2 py-1 text-[10px] font-medium text-white shadow backdrop-blur"
                     classList={{
                       "ring-1 ring-warn/70": !mppCalibrated(),
                     }}
                     title={mppCalibrated()
                       ? `Physical scale: ${formatMicron(nice.lengthUm)} at ${currentMag().toFixed(1)}× (native ${nativeMag().toFixed(0)}×)`
                       : `Slide is uncalibrated — no PixelSpacing in DICOM. Values assume ${FALLBACK_MPP} µm/pixel.`}>
                  {/* Segmented bar — four alternating black/white
                      blocks so the eye can count halves at a glance.
                      The outer border is a crisp 1px white line for
                      contrast on any tissue colour. */}
                  <div class="relative h-2 overflow-hidden rounded-sm border border-white/80"
                       style={{ width: `${barPx}px` }}>
                    <div class="absolute top-0 h-full bg-white" style={{ left: "0%",  width: "25%" }} />
                    <div class="absolute top-0 h-full bg-black" style={{ left: "25%", width: "25%" }} />
                    <div class="absolute top-0 h-full bg-white" style={{ left: "50%", width: "25%" }} />
                    <div class="absolute top-0 h-full bg-black" style={{ left: "75%", width: "25%" }} />
                  </div>
                  <span class="font-mono">
                    {mppCalibrated() ? "" : "≈ "}{formatMicron(nice.lengthUm)}
                  </span>
                </div>
              </div>
            );
          })()}
        </Show>

        {/* Magnification objective picker — always-visible overlay
            at the top-center of the viewport. Variant switches on
            `fullscreen`, not on viewport width, because the
            enclosing StudyViewer squeezes the pathology column into
            ~90 px of horizontal space in the normal layout — a full
            horizontal row of objectives gets clipped by the
            container's overflow-hidden. In fullscreen the shell
            breaks out to `fixed inset-0` (see PathologyShell) so the
            row fits comfortably at the top-centre. */}
        <div class="pointer-events-none absolute left-1/2 top-2 z-20 -translate-x-1/2">
          <Show when={p.fullscreen}
                fallback={
                  <MagObjectiveDropdown current={currentMag()}
                                        digital={isDigitalZoom()}
                                        nativeMag={nativeMag()}
                                        onPick={(x) => setMag(x)} />
                }>
            <MagObjectiveRow current={currentMag()}
                             digital={isDigitalZoom()}
                             nativeMag={nativeMag()}
                             onPick={(x) => setMag(x)} />
          </Show>
        </div>

        {/* Minimap — hideable + draggable. Default corner = bottom-
            right. On mobile + fullscreen we push it ~20 mm above the
            bottom edge so Android Firefox's URL bar (which sits at
            the bottom in portrait) doesn't cover it; `env(safe-area-
            inset-bottom)` gets added too for iOS home-bar devices.
            The user can grab the "overview" title strip and drag the
            panel anywhere on screen — `miniPos` takes over once that
            happens and the default classes step aside. */}
        <Show when={miniOpen()} fallback={
          <button type="button"
                  class="fixed z-40 rounded border border-ink-300 bg-black/85 px-1.5 py-1 text-[10px] text-white/70 backdrop-blur hover:text-white"
                  style={{
                    right:  `${(p.rightReserve ?? 0) + 12}px`,
                    bottom: `calc(env(safe-area-inset-bottom, 0px) + ${window.matchMedia("(max-width: 767px)").matches && p.fullscreen ? "20mm" : "12px"})`,
                  }}
                  title="Show minimap"
                  onClick={() => setMiniOpen(true)}>◰</button>
        }>
          <div ref={miniFrame}
               class="fixed z-40 rounded border border-ink-300 bg-black/85 p-1 backdrop-blur"
               style={miniPos()
                 ? { left: `${miniPos()!.x}px`, top: `${miniPos()!.y}px` }
                 : {
                     right:  `${(p.rightReserve ?? 0) + 12}px`,
                     bottom: `calc(env(safe-area-inset-bottom, 0px) + ${window.matchMedia("(max-width: 767px)").matches && p.fullscreen ? "20mm" : "12px"})`,
                   }}>
            {/* Title strip is the drag handle. `touch-none` so touch
                drags don't also try to scroll the page. */}
            <div class="mb-1 flex cursor-move items-center justify-between px-1 text-[9px] uppercase text-ink-700 touch-none select-none"
                 title="Drag to reposition"
                 onPointerDown={onMiniGripDown}
                 onPointerMove={onMiniGripMove}
                 onPointerUp={onMiniGripUp}
                 onPointerCancel={onMiniGripUp}>
              <span class="pointer-events-none">overview</span>
              <button type="button"
                      class="pointer-events-auto text-[12px] text-ink-700 hover:text-white"
                      title="Hide minimap"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => setMiniOpen(false)}>×</button>
            </div>
            <canvas ref={miniCvs} class="block cursor-crosshair" onClick={onMinimap} title="Click to jump" />
          </div>
        </Show>

        {/* Only show the "Indexing…" card when the resource truly has
            no data yet. Using `!pyr()` instead of `pyr.loading` means
            the overlay disappears as soon as the pyramid has resolved
            once — stale-while-revalidate refetches don't bring it
            back. */}
        <Show when={!pyr()}>
          <div class="pointer-events-none absolute inset-0 grid place-items-center">
            <div class="rounded-lg border border-ink-300 bg-black/80 px-5 py-4 text-center text-[11px] text-white/80 backdrop-blur">
              <div class="mb-1 text-[18px]">🔬</div>
              <div>Indexing pyramid…</div>
            </div>
          </div>
        </Show>

        <div class="pointer-events-none absolute left-3 top-3 rounded bg-black/70 px-2 py-1 font-mono text-[10px] text-white/80 backdrop-blur">
          ({cursorX().toFixed(0)}, {cursorY().toFixed(0)}) · {currentMag().toFixed(1)}×{isDigitalZoom() ? " digital" : ""}
        </div>
      </div>
    </div>
  );
}

function mkBase<K extends PathologyTool>(
  kind: K, cls: AC, shape: Partial<PathAnno>,
): PathAnno {
  return { id: crypto.randomUUID(), kind, color: cls.color, label: cls.label,
           source: "human", ...shape } as PathAnno;
}

/** Collect the ring of neighbour tile coordinates around the visible
 *  set, filtered to legal indices and deduplicated against the
 *  visible set. Pure coordinates — the actual fetch goes through
 *  getTilesBatch so the ring is one batched HTTP request, not a
 *  handful of singletons. */
function collectRingCoords(
  level: PyramidLevel,
  visible: Array<{ tx: number; ty: number }>,
  ring: number,
): Array<{ tx: number; ty: number }> {
  const seen = new Set<string>();
  for (const v of visible) seen.add(`${v.tx},${v.ty}`);
  const out: Array<{ tx: number; ty: number }> = [];
  for (const v of visible) {
    for (let dy = -ring; dy <= ring; dy++) {
      for (let dx = -ring; dx <= ring; dx++) {
        if (dx === 0 && dy === 0) continue;
        const tx = v.tx + dx, ty = v.ty + dy;
        if (tx < 0 || ty < 0 || tx >= level.tilesPerRow || ty >= level.tilesPerCol) continue;
        const k = `${tx},${ty}`;
        if (seen.has(k)) continue;
        seen.add(k);
        out.push({ tx, ty });
      }
    }
  }
  return out;
}

/** Tiny top-right toggle that takes the viewport in and out of
 *  fullscreen. Always visible — in fullscreen it's the only way back
 *  to the chrome. Positioned inside the right gutter so it clears the
 *  minimap corner and annotation rail on desktop. */
function FullscreenToggle(p: { fullscreen: boolean; onClick?: () => void }) {
  return (
    <button type="button"
            class="absolute right-2 top-2 z-20 grid h-7 w-7 place-items-center rounded-md border border-ink-300 bg-black/70 text-[14px] leading-none text-white/85 shadow-md backdrop-blur hover:bg-ink-200 hover:text-white"
            title={p.fullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen"}
            aria-pressed={p.fullscreen}
            onClick={() => p.onClick?.()}>
      {p.fullscreen ? "⤢" : "⛶"}
    </button>
  );
}

function ToolBtn(p: {
  onClick: () => void; active?: boolean; disabled?: boolean;
  k?: string; label: string; children: any;
}) {
  return (
    <button type="button" disabled={p.disabled}
            class="grid h-9 w-9 place-items-center rounded text-[18px] text-white/80 transition"
            classList={{
              "bg-brand text-white": !!p.active,
              "hover:bg-ink-300 hover:text-white": !p.active && !p.disabled,
              "opacity-30": !!p.disabled,
            }}
            title={`${p.label}${p.k ? "  (" + p.k + ")" : ""}`}
            onClick={p.onClick}>
      {p.children}
    </button>
  );
}

function AnnoShape(props: { a: PathAnno; active: boolean; strokePx: number; mppX: number; calibrated: boolean }) {
  // All reads go through `props.a.*` so Solid tracks the underlying
  // annotation object. The previous `const a = props.a` captured a
  // stale reference at component mount — when the user edited the
  // label via store.replace(), the store rebuilt the item with a
  // new reference but AnnoShape had already cached the old one, so
  // the on-slide label halo never updated.
  const stroke = () => props.active ? "#ffffff" : props.a.color;
  const width  = () => props.strokePx * (props.active ? 2.0 : 1.0);
  const fillCol = () => props.a.color + "20";
  const fmtUm = (um: number): string => {
    const aa = Math.abs(um);
    if (aa < 1)    return `${um.toFixed(2)} µm`;
    if (aa < 1000) return `${um.toFixed(aa < 10 ? 1 : 0)} µm`;
    const mm = um / 1000;
    return `${mm < 10 ? mm.toFixed(2) : mm < 100 ? mm.toFixed(1) : mm.toFixed(0)} mm`;
  };
  const unitSuffix = () => props.calibrated ? "" : " (~)";

  // Rect / ellipse / ruler share an (a, b) pair. We read props.a with
  // a cast where the kind has been narrowed to allow property access.
  const asRect = () => props.a as Extract<PathAnno, { kind: "rect" }>;
  const asEll  = () => props.a as Extract<PathAnno, { kind: "ellipse" }>;
  const asRul  = () => props.a as Extract<PathAnno, { kind: "ruler" }>;
  const asPoly = () => props.a as Extract<PathAnno, { kind: "polygon" }>;
  const asFree = () => props.a as Extract<PathAnno, { kind: "freehand" }>;
  const asPt   = () => props.a as Extract<PathAnno, { kind: "point" }>;
  const asTxt  = () => props.a as Extract<PathAnno, { kind: "text" }>;

  return <>
    <Show when={props.a.kind === "rect"}>
      {(() => {
        const x = () => Math.min(asRect().a.x, asRect().b.x);
        const y = () => Math.min(asRect().a.y, asRect().b.y);
        const w = () => Math.abs(asRect().b.x - asRect().a.x);
        const h = () => Math.abs(asRect().b.y - asRect().a.y);
        const sizeText = () => `${fmtUm(w() * props.mppX)} × ${fmtUm(h() * props.mppX)}${unitSuffix()}`;
        return <>
          <rect x={x()} y={y()} width={w()} height={h()} fill={fillCol()} stroke={stroke()} stroke-width={width()} />
          <Show when={props.a.label}>
            <LabelHalo x={x()} y={y() - 6 * props.strokePx} text={props.a.label!} scale={props.strokePx} />
          </Show>
          {/* Size label is ALWAYS visible — was previously gated on
              active so pathologists couldn't compare dimensions at
              a glance. Size reads from mppX so the µm label is
              physically accurate on calibrated slides. */}
          <LabelHalo x={x() + w() + 4 * props.strokePx} y={y() + h()} text={sizeText()} scale={props.strokePx} />
        </>;
      })()}
    </Show>
    <Show when={props.a.kind === "ellipse"}>
      {(() => {
        const cx = () => (asEll().a.x + asEll().b.x) / 2;
        const cy = () => (asEll().a.y + asEll().b.y) / 2;
        const rx = () => Math.abs(asEll().b.x - asEll().a.x) / 2;
        const ry = () => Math.abs(asEll().b.y - asEll().a.y) / 2;
        const sizeText = () => `${fmtUm(2 * rx() * props.mppX)} × ${fmtUm(2 * ry() * props.mppX)}${unitSuffix()}`;
        return <>
          <ellipse cx={cx()} cy={cy()} rx={rx()} ry={ry()} fill={fillCol()} stroke={stroke()} stroke-width={width()} />
          <Show when={props.a.label}>
            <LabelHalo x={cx() - rx()} y={cy() - ry() - 4 * props.strokePx} text={props.a.label!} scale={props.strokePx} />
          </Show>
          <LabelHalo x={cx() + rx() + 4 * props.strokePx} y={cy()} text={sizeText()} scale={props.strokePx} />
        </>;
      })()}
    </Show>
    <Show when={props.a.kind === "polygon"}>
      {(() => {
        const pts = () => asPoly().points.map((q) => `${q.x},${q.y}`).join(" ");
        const firstPt = () => asPoly().points[0];
        return <>
          <polygon points={pts()} fill={fillCol()} stroke={stroke()} stroke-width={width()} />
          <Show when={props.a.label && firstPt()}>
            <LabelHalo x={firstPt().x} y={firstPt().y - 6 * props.strokePx} text={props.a.label!} scale={props.strokePx} />
          </Show>
        </>;
      })()}
    </Show>
    <Show when={props.a.kind === "freehand"}>
      {(() => {
        const pts = () => asFree().points.map((q) => `${q.x},${q.y}`).join(" ");
        const firstPt = () => asFree().points[0];
        return <>
          <polyline points={pts()} fill="none" stroke={stroke()} stroke-width={width()} />
          <Show when={props.a.label && firstPt()}>
            <LabelHalo x={firstPt().x} y={firstPt().y - 6 * props.strokePx} text={props.a.label!} scale={props.strokePx} />
          </Show>
        </>;
      })()}
    </Show>
    <Show when={props.a.kind === "point"}>
      {(() => (
        <>
          <circle cx={asPt().at.x} cy={asPt().at.y} r={5 * props.strokePx} fill={stroke()} />
          <Show when={props.a.label}>
            <LabelHalo x={asPt().at.x + 6 * props.strokePx} y={asPt().at.y} text={props.a.label!} scale={props.strokePx} />
          </Show>
        </>
      ))()}
    </Show>
    <Show when={props.a.kind === "ruler"}>
      {(() => {
        const distUm = () => Math.hypot(asRul().b.x - asRul().a.x, asRul().b.y - asRul().a.y) * props.mppX;
        const mx = () => (asRul().a.x + asRul().b.x) / 2;
        const my = () => (asRul().a.y + asRul().b.y) / 2 - 6 * props.strokePx;
        const displayText = () => (props.a.label ? props.a.label + " · " : "") + fmtUm(distUm()) + unitSuffix();
        return <>
          <line x1={asRul().a.x} y1={asRul().a.y} x2={asRul().b.x} y2={asRul().b.y} stroke={stroke()} stroke-width={width()} />
          <circle cx={asRul().a.x} cy={asRul().a.y} r={3 * props.strokePx} fill={stroke()} />
          <circle cx={asRul().b.x} cy={asRul().b.y} r={3 * props.strokePx} fill={stroke()} />
          <LabelHalo x={mx()} y={my()} text={displayText()} scale={props.strokePx} />
        </>;
      })()}
    </Show>
    <Show when={props.a.kind === "text"}>
      <LabelHalo x={asTxt().at.x} y={asTxt().at.y} text={asTxt().text ?? props.a.label ?? ""} scale={props.strokePx} />
    </Show>
    {/* `kind === "snapshot"` is intentionally NOT rendered on the
        slide — a snapshot is a viewport-level capture used for the
        Annotations panel thumbnail + report-illustration export,
        not a geometric primitive on the tissue. */}
  </>;
}

/** Stroke-halo text label. Borrowed from the Radiology annotation
 *  layer pattern (`src/viewport/AnnotationLayer.tsx` → `Label()`):
 *  render a dark outline BEHIND the bright glyph fill via
 *  `paint-order="stroke fill"`. The halo keeps the label readable on
 *  white H&E tissue, bright IHC stain, or a dark viewport background
 *  — all equally possible in a pathology workflow. The stroke width
 *  scales with the annotation's own strokePx so it holds up across
 *  zoom levels. */
function LabelHalo(p: { x: number; y: number; text: string; scale: number }) {
  if (!p.text) return null;
  const fs = 11 * p.scale;
  const sw = Math.max(2.5 * p.scale, 2);
  return (
    <text x={p.x} y={p.y}
          fill="#F8FAFC"
          stroke="#020617"
          stroke-width={sw}
          stroke-linejoin="round"
          paint-order="stroke fill"
          font-size={fs}
          font-weight="600"
          font-family="JetBrains Mono,ui-monospace,monospace">{p.text}</text>
  );
}
